/**
 * Composant Button
 * Bouton stylisé avec différentes variantes
 */

import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

const buttonVariants = cva(
    // Base styles with smooth transitions
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ease-out',
    {
        variants: {
            variant: {
                default: 'bg-[#0F2744] text-white hover:bg-[#091A2E] hover:shadow-md hover:shadow-[#0F2744]/20 focus-visible:ring-[#0F2744] dark:bg-[#3B82F6] dark:hover:bg-[#2563EB]',
                destructive: 'bg-[#EF4444] text-white hover:bg-[#DC2626] hover:shadow-md hover:shadow-[#EF4444]/20 focus-visible:ring-[#EF4444]',
                outline: 'border border-[#E2E8F0] bg-white hover:bg-[#F8FAFC] hover:border-[#CBD5E1] text-[#1E293B] dark:border-[#334155] dark:bg-[#1E293B] dark:hover:bg-[#334155] dark:text-[#F1F5F9]',
                secondary: 'bg-[#F1F5F9] text-[#1E293B] hover:bg-[#E2E8F0] dark:bg-[#334155] dark:text-[#F1F5F9] dark:hover:bg-[#475569]',
                ghost: 'hover:bg-[#F1F5F9] text-[#64748B] hover:text-[#1E293B] dark:hover:bg-[#334155] dark:text-[#94A3B8] dark:hover:text-[#F1F5F9]',
                link: 'text-[#1D4ED8] underline-offset-4 hover:underline dark:text-[#60A5FA]',
                success: 'bg-[#10B981] text-white hover:bg-[#059669] hover:shadow-md hover:shadow-[#10B981]/20 focus-visible:ring-[#10B981]',
                warning: 'bg-[#F59E0B] text-white hover:bg-[#D97706] hover:shadow-md hover:shadow-[#F59E0B]/20 focus-visible:ring-[#F59E0B]',
            },
            size: {
                default: 'h-10 px-4 py-2',
                sm: 'h-8 rounded-md px-3 text-xs',
                lg: 'h-12 rounded-lg px-8 text-base',
                icon: 'h-9 w-9 rounded-lg',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    }
);

export interface ButtonProps
    extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
    isLoading?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, isLoading, children, disabled, ...props }, ref) => {
        return (
            <button
                className={cn(buttonVariants({ variant, size, className }))}
                ref={ref}
                disabled={disabled || isLoading}
                {...props}
            >
                {isLoading && <Loader2 className="h-4 w-4 spinner" />}
                {children}
            </button>
        );
    }
);

Button.displayName = 'Button';

export { Button, buttonVariants };
